
import React from 'react';
import UserRoleDialog from './UserRoleDialog';
import InviteUserDialog from './InviteUserDialog';
import PasswordResetDialog from './PasswordResetDialog';
import DeleteUserDialog from './DeleteUserDialog';
import { UserWithRole, UserRole } from '@/pages/admin/types';

interface UserManagementDialogsProps {
  // Role Dialog Props
  isEditDialogOpen: boolean;
  setIsEditDialogOpen: (open: boolean) => void;
  selectedUser: UserWithRole | null;
  selectedRole: UserRole;
  setSelectedRole: (role: UserRole) => void;
  onUpdateRole: () => void;
  isSuperAdmin: boolean;

  // Invite Dialog Props
  isInviteDialogOpen: boolean;
  setIsInviteDialogOpen: (open: boolean) => void;
  onInviteUser: (userData: { email: string; name: string; role: UserRole }) => Promise<boolean>;
  isSubmittingInvite: boolean;

  // Password Reset Dialog Props
  isPasswordResetDialogOpen: boolean;
  setIsPasswordResetDialogOpen: (open: boolean) => void;
  selectedUserForPasswordReset: UserWithRole | null;
  onResetPassword: (userData: { userId: string; newPassword: string }) => Promise<boolean>;
  isSubmittingPasswordReset: boolean;

  // Delete Dialog Props
  isDeleteDialogOpen: boolean;
  setIsDeleteDialogOpen: (open: boolean) => void;
  selectedUserForDelete: UserWithRole | null;
  onDeleteUser: (userId: string) => Promise<boolean>;
  isDeleting: boolean;
}

const UserManagementDialogs: React.FC<UserManagementDialogsProps> = ({
  isEditDialogOpen,
  setIsEditDialogOpen,
  selectedUser,
  selectedRole,
  setSelectedRole,
  onUpdateRole,
  isSuperAdmin,
  isInviteDialogOpen,
  setIsInviteDialogOpen,
  onInviteUser,
  isSubmittingInvite,
  isPasswordResetDialogOpen,
  setIsPasswordResetDialogOpen,
  selectedUserForPasswordReset,
  onResetPassword,
  isSubmittingPasswordReset,
  isDeleteDialogOpen,
  setIsDeleteDialogOpen,
  selectedUserForDelete,
  onDeleteUser,
  isDeleting
}) => {
  const handleInviteUser = async (userData: { email: string; name: string; role: UserRole }) => {
    const success = await onInviteUser(userData);
    if (success) {
      setIsInviteDialogOpen(false);
    }
  };

  const handlePasswordReset = async (userData: { userId: string; newPassword: string }) => {
    const success = await onResetPassword(userData);
    if (success) {
      setIsPasswordResetDialogOpen(false);
    }
  };

  return (
    <>
      <UserRoleDialog
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        selectedUser={selectedUser}
        selectedRole={selectedRole}
        onRoleChange={(value: string) => setSelectedRole(value as UserRole)}
        onUpdateRole={() => {
          onUpdateRole();
          setIsEditDialogOpen(false);
        }}
        isSuperAdmin={isSuperAdmin}
      />

      <InviteUserDialog
        isOpen={isInviteDialogOpen}
        onOpenChange={setIsInviteDialogOpen}
        onInviteUser={handleInviteUser}
        isSubmitting={isSubmittingInvite}
      />

      <PasswordResetDialog
        isOpen={isPasswordResetDialogOpen}
        onOpenChange={setIsPasswordResetDialogOpen}
        selectedUser={selectedUserForPasswordReset}
        onResetPassword={handlePasswordReset}
        isSubmitting={isSubmittingPasswordReset}
      />

      <DeleteUserDialog
        isOpen={isDeleteDialogOpen}
        setIsOpen={setIsDeleteDialogOpen}
        user={selectedUserForDelete}
        onConfirmDelete={onDeleteUser}
        isDeleting={isDeleting}
      />
    </>
  );
};

export default UserManagementDialogs;
